<template>
  <div>
    <gmap-map
      class="map"
      ref="map"
      :center="{ lat: 38.552704, lng: -90.082259 }"
      :zoom="5"
      :options="MapOptions"
    ></gmap-map>

    <div class="region-list box" v-if="!region">
      <div class="header">
        <h1 class="title is-4 has-text-centered">Regions</h1>
        <hr>
      </div>

      <div class="list">
        <div :is="SearchCardComponent" v-for="region in regions" :key="region.id" :entity="region" v-if="regions.length > 0" hide-default-controls>
          <button slot="controls" class="button is-danger" @click="onDelete(region)">Delete</button>
        </div>
      </div>

      <div class="controls">
        <hr>
        <nav class="level">
          <div class="level-left"></div>
          <div class="level-right">
            <div class="level-item">
              <button class="button is-success" @click="load">Refresh</button>
            </div>
            <div class="level-item">
              <button class="button is-primary" @click="showDistricts = true">Create Region</button>
            </div>
          </div>
        </nav>
      </div>
    </div>

    <modal @close="showDistricts = false" v-if="showDistricts">
      <div class="box">
        <form @submit.prevent="onStartCreate">
          <div class="field">
            <label>Name</label>
            <div class="control">
              <input type="text" class="input" name="name">
            </div>
          </div>

          <div class="field">
            <label>District</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select name="district">
                  <option v-for="district in districts" :key="district.id" :value="district.id">{{ district.name }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="buttons is-right">
            <button class="button is-primary">Create</button>
          </div>
        </form>
      </div>
    </modal>

    <button type="button" class="button is-danger is-action" id="stop-creating-btn" v-if="region" @click="onFinishCreate">
      <fa-icon icon="stop"></fa-icon>
    </button>
  </div>
</template>

<script>
import Counties from '@/assets/data/counties'
import Library from 'library'

export default {
  name: 'RegionsSearch',

  data () {
    return {
      districts: [],
      regions: [],
      counties: [],

      showDistricts: false,
      region: null,
      isEditting: false
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      const loader = this.$loading.open()
      try {
        if (this.counties) {
          this.counties.forEach(county => {
            county.dataLayer.setMap(null)
          })
        }

        let [districts, regions, counties] = await Promise.all([
          this.$entity.query.all('District', {}, ['States']),
          this.$entity.query.all('Region', {}, ['Counties']),
          this.$entity.query.all('County', {})
        ])

        this.districts = districts.data || []
        this.regions = regions.data || []
        await this.$refs.map.$mapPromiseDefferred

        this.counties = counties.data.filter(county => {
          const g = Counties.features.find(c => c.properties.GEO_ID.includes(county.fipsCode))

          county.dataLayer = g

          return g
        }).map(county => {
          const dataLayer = new google.maps.Data()
          dataLayer.addGeoJson(county.dataLayer)
          
          return {
            ...county,
            dataLayer
          }
        })

        this.regions.forEach(region => {
          const regionColor = Library.Functions.getRandomColor()
          region.counties.forEach(county => {
            const c = this.counties.find(c => c.id === county.id)

            if (c) {
              c.dataLayer.setStyle({
                fillColor: regionColor
              })
              c.dataLayer.setMap(this.$refs.map.$mapObject)
            }
          })
        })
      } catch (e) {
        console.error(e)
        this.$handleError('There was a problem loading in the region data');
      } finally {
        loader.close()
      }
    },

    async onStartCreate (e) {
      const district = this.districts.find(district => district.id == e.target.elements.district.value)

      if (district) {
        this.counties.forEach(county => {
          county.dataLayer.setMap(null)
        })

        const usedCounties = Array.prototype.concat.apply([], this.regions.map(region => region.counties.map(rc => rc.countyId)))
        const districtStates = district.states.map(state => state.id)
        const counties = this.counties
          .filter(county => districtStates.includes(county.stateId))
          .filter(county => !usedCounties.includes(county.id))

        this.region = {
          name: e.target.elements.name.value,
          districtId: district.id,
          counties: []
        }

        counties.forEach(county => {
          county.dataLayer.setMap(this.$refs.map.$mapObject)

          county.dataLayer.addListener('click', () => {
            if (this.region.counties.includes(county.id)) {
              this.region.counties.splice(
                this.region.counties.indexOf(county.id),
                1
              )
              county.dataLayer.setStyle({
                fillColor: null
              })
            } else {
              this.region.counties.push(county.id)
              county.dataLayer.setStyle({
                fillColor: 'orange'
              })
            }
          })
        })

        this.showDistricts = false
      } else {
        this.$handleError('That is not a district')
        this.showDistricts = false
      }
    },

    async onStartEdit (region) {
      
    },

    async onFinishCreate () {
      const loader = this.$loading.open()
      try {
        const response = await this.$entity.create('Region', {
          name: this.region.name,
          districtId: this.region.districtId
        });
        await this.$http.post(`Regions/${response.data.id}/Counties`, this.region.counties)

        this.region = null;
        this.load()
      } catch (e) {
        console.error(e)
        this.$handleError('There was an error creating your region.', e.response.data)
      } finally {
        loader.close()
      }
    },

    async onDelete (region) {
      this.$dialog.confirm({
        message: `Do you want to delete ${region.name}?`,
        onConfirm: async () => {
          const loader = this.$loading.open()
          try {
            this.$entity.remove('Region', region.id)
            this.load()
          } catch (e) {
            console.error(e)
            this.$handleError('There was an error deleting that region', e.response.data)
          } finally {
            loader.close()
          }
        }
      })
    }
  },

  computed: {
    MapOptions () {
      return {
        fullscreenControl: false,
        streetViewControl: false,
        zoomControlOptions: {
          position: 6 // Left Bottom
        }
      }
    },

    SearchCardComponent () {
      return Library.Components.SearchCards.Region
    }
  },

  components: {
    Modal: Library.Components.Modal
  }
}
</script>

<style lang="scss" scoped>
  .map {
    position: absolute;
    top: 0px;
    right: 0px;
    left: 0px;
    bottom: 0px;
  }

  .region-list {
    position: absolute;
    top: 1rem;
    right: 1rem;
    bottom: 1rem;
    display: flex;
    flex-direction: column;

    width: 350px;

    .header {
      flex: 0 0 auto;
    }

    .list {
      flex: 1 1 auto;
      overflow-y: auto;
      overflow-x: visible;
      margin-left: -1rem;
      margin-right: -1rem;
      padding: .5rem;
    }

    .controls {
      flex: 0 0 auto;
    }
  }

  .button.is-action {
    width: 4rem;
    height: 4rem;
    border-radius: 2rem;
    font-size: 1.5rem;
  }

  #stop-creating-btn {
    position: absolute;
    right: 3rem;
    bottom: 1rem;
  }
</style>
